import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { AuthStatusIcon } from '../components/auth/AuthStatusIcon';
import { resendVerification, verifyEmail } from '../lib/api';
import { useAuthStore } from '../store/auth';

/**
 * Landing point for the emailed verification link (`/verify-email?token=…`).
 * Verifies the token, then hands off to the app — the OnboardingGate routes the
 * user onward (into onboarding or the app). Expired/invalid links offer a resend
 * path instead of dead-ending. Coordinates with onboarding's VerifyStep, which
 * polls and auto-advances once this page verifies the token.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const status = useAuthStore((s) => s.status);
  const navigate = useNavigate();

  const [state, setState] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) {
      setState('error');
      return;
    }
    verifyEmail(token)
      .then(async () => {
        await bootstrap(); // refresh the session so emailVerified is up to date
        setState('success');
      })
      .catch(() => setState('error'));
  }, [token, bootstrap]);

  async function resend() {
    setResending(true);
    try {
      await resendVerification();
      setResent(true);
    } catch {
      /* best-effort — keep the UI calm */
    } finally {
      setResending(false);
    }
  }

  return (
    <Card className="flex flex-col items-center gap-4 p-8 text-center">
      {state === 'verifying' && (
        <>
          <Spinner size="lg" />
          <p className="text-sm text-muted">Confirming your email…</p>
        </>
      )}

      {state === 'success' && (
        <>
          <AuthStatusIcon variant="success" />
          <div>
            <h1 className="font-display text-xl font-bold text-bone">You're verified</h1>
            <p className="mt-1 text-sm text-muted">
              {status === 'authenticated'
                ? 'Your email is confirmed — pick up right where you left off.'
                : 'Your email is confirmed. Head back to the tab where you started your setup, or sign in to continue here.'}
            </p>
          </div>
          <Button
            onClick={() =>
              navigate(status === 'authenticated' ? '/onboarding' : '/login', { replace: true })
            }
            className="mt-1 w-full"
          >
            {status === 'authenticated' ? 'Continue setting up' : 'Log in to continue'}
          </Button>
        </>
      )}

      {state === 'error' && (
        <>
          <AuthStatusIcon variant="error" />
          <div>
            <h1 className="font-display text-xl font-bold text-bone">This link didn't work</h1>
            <p className="mt-1 text-sm text-muted">
              It may have expired or already been used. Get a fresh one below.
            </p>
          </div>
          {status === 'authenticated' ? (
            <Button
              variant="outline"
              disabled={resending || resent}
              onClick={resend}
              className="mt-1 w-full"
            >
              {resent ? 'New link sent ✓' : resending ? 'Sending…' : 'Resend verification email'}
            </Button>
          ) : (
            <Link
              to="/login"
              className="mt-1 w-full rounded-sm border border-border px-4 py-2 text-center text-sm font-semibold text-bone transition-colors hover:bg-surface-2"
            >
              Log in to get a new link
            </Link>
          )}
        </>
      )}
    </Card>
  );
}
